// Screen-name navigation.
//
// Screens navigate by NAME (`navigate('patient', { caseId })`), never by URL
// literal. `src/constants/routes.js` owns the name → path map and the <Route>
// patterns App.jsx mounts; this hook is the React half of it, and nothing else.
//
// The map is NOT re-declared here. It used to be, and the day the route table
// grew a visit and a chat page there were two lists to keep in step — the exact
// drift tests/routing.test.js exists to catch. `ROUTES` is re-exported so the
// call sites that import it from this module keep working.
//
// tests/routing.test.js pins the three-way agreement: every ROUTES key builds a
// path some <Route> in App.jsx matches, every declared <Route> is reachable
// through a key, and every NAV_ITEMS id is one of those keys.

import React from 'react';
import { useNavigate } from 'react-router-dom';

import { ROUTES, ROUTE_PATTERNS, DEFAULT_RECORD_TAB } from '../constants/routes.js';

export { ROUTES, ROUTE_PATTERNS, DEFAULT_RECORD_TAB };

export function useAppNavigate() {
  const navigate = useNavigate();

  /**
   * @param screen  a key of ROUTES
   * @param data    the route's own parameters. `replace: true` swaps the current
   *                history entry instead of pushing one — for a NAVIGATION THE
   *                USER DID NOT MAKE, such as the record correcting a stale
   *                `:tab` segment, or the chat page adopting the id of the
   *                thread the first question just created. Pushed, the back
   *                button would return the student to the broken URL, which
   *                would correct itself again — a trap they cannot leave by
   *                going back.
   */
  return React.useCallback(
    (screen, data) => {
      const build = ROUTES[screen];
      if (!build) {
        // Loud rather than silent: a typo'd screen name is a dead button, and a
        // dead button is indistinguishable from a slow one.
        console.warn(`[useAppNavigate] no route for "${screen}"`);
        return;
      }
      navigate(build(data || {}), { state: data, replace: !!data?.replace });
      window.scrollTo(0, 0);
    },
    [navigate],
  );
}

export default useAppNavigate;
